/**
 * Super Admin · PFP Pending Invites
 *
 * When a school admin invites parents but some never redeem (30-day TTL
 * expired), the invite link stops working. This page lets a super admin:
 *   - pick any PFP-sponsored school
 *   - see every parent who hasn't completed signup
 *   - reissue a fresh 30-day invite link for all of them in one click
 *   - copy each invite URL to paste into email / WhatsApp / SMS
 *
 * Backend: /api/admin/pfp-schools, /api/admin/pfp-schools/:id/pending-parents,
 * /api/admin/pfp-schools/:id/resend-invites.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Copy, RefreshCw, School, Users, AlertTriangle, Check,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb 2026 — FastAPI 422 responses carry `detail` as an array of
// validation error objects which React cannot render inside a toast
// (and crashed the whole page). Always coerce to a string.
const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) {
    return d.map((x) => x?.msg || x?.message || JSON.stringify(x)).join(', ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return e?.message || fallback;
};

export default function AdminPFPPendingInvites() {
  const [schools, setSchools] = useState([]);
  const [selected, setSelected] = useState(null); // school obj
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [invites, setInvites] = useState([]); // last reissue result
  const [copiedId, setCopiedId] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadSchools = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pfp-schools`, { headers: authHeaders() });
      setSchools(r.data || []);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load schools'));
    } finally {
      setLoading(false);
    }
  };

  const loadPending = async (school) => {
    setSelected(school);
    setInvites([]);
    try {
      const r = await axios.get(
        `${API}/admin/pfp-schools/${school.id}/pending-parents`,
        { headers: authHeaders() },
      );
      setPending(r.data?.pending_parents || []);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load pending parents'));
    }
  };

  const reissueAll = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Reissue a fresh invite link for all ${pending.length} pending parent${pending.length === 1 ? '' : 's'} at ${selected.name}?`
    )) return;
    setWorking(true);
    try {
      const r = await axios.post(
        `${API}/admin/pfp-schools/${selected.id}/resend-invites`,
        { emails: null },
        { headers: authHeaders() },
      );
      setInvites(r.data?.invites || []);
      toast.success(`${r.data?.reissued || 0} invite${r.data?.reissued === 1 ? '' : 's'} ready to copy`);
      // Refresh pending-parent token_status WITHOUT clearing the fresh
      // invite URLs we just rendered (loadPending clears `invites`).
      try {
        const r2 = await axios.get(
          `${API}/admin/pfp-schools/${selected.id}/pending-parents`,
          { headers: authHeaders() },
        );
        setPending(r2.data?.pending_parents || []);
      } catch { /* non-fatal — the invite URLs are already on screen */ }
    } catch (e) {
      toast.error(errMsg(e, 'Failed to reissue invites'));
    } finally {
      setWorking(false);
    }
  };

  const copyOne = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error('Could not copy — select the URL manually');
    }
  };

  const copyAll = async () => {
    if (!invites.length) return;
    const text = invites
      .filter((i) => i.status === 'ready')
      .map((i) => `${i.name || i.email}\n${i.email}\n${i.invite_url}\n`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('All invite URLs copied');
    } catch {
      toast.error('Could not copy — try selecting manually');
    }
  };

  useEffect(() => { loadSchools(); }, []);

  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-6" data-testid="admin-pfp-pending-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-serif text-stone-900">PFP · Pending parent invites</h1>
            <p className="text-sm text-stone-600 mt-1">
              Parents who were invited to a sponsored school but haven't finished setting up their account. Click a school to see pending parents and reissue fresh 30-day invite links.
            </p>
          </div>
          <Button variant="outline" onClick={loadSchools} data-testid="refresh-schools-btn" className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-stone-500 text-sm py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading schools…
          </div>
        ) : schools.length === 0 ? (
          <Card className="p-6 text-sm text-stone-600">No PFP-sponsored schools found.</Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {schools.map((s) => (
              <button
                key={s.id}
                onClick={() => loadPending(s)}
                className={`text-left rounded-2xl border p-4 bg-white hover:shadow transition
                  ${selected?.id === s.id ? 'border-primary ring-2 ring-primary/20' : 'border-stone-200'}`}
                data-testid={`school-card-${s.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <School className="w-4 h-4 text-primary" />
                  <span className="font-medium text-stone-900">{s.name}</span>
                </div>
                <div className="text-xs text-stone-500 flex flex-wrap gap-2 mt-2">
                  <span><Users className="w-3 h-3 inline mr-1" />{s.parents} parent{s.parents === 1 ? '' : 's'}</span>
                  <span>{s.seats_used}/{s.seats_purchased} seats used</span>
                  {s.pending_parents > 0 && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                      {s.pending_parents} pending
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <Card className="p-5 bg-white" data-testid="pending-parents-panel">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <h2 className="text-lg font-serif text-stone-900">{selected.name}</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  {pending.length} parent{pending.length === 1 ? '' : 's'} haven't finished signing up.
                </p>
              </div>
              {pending.length > 0 && (
                <Button
                  onClick={reissueAll}
                  disabled={working}
                  data-testid="reissue-all-btn"
                  className="rounded-full"
                >
                  {working ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Reissue all invites
                </Button>
              )}
            </div>

            {pending.length === 0 ? (
              <p className="text-sm text-stone-500 py-6 text-center">
                No pending parents 🎉 Everyone has completed signup.
              </p>
            ) : (
              <ul className="divide-y divide-stone-100">
                {pending.map((p) => {
                  const inv = invites.find((i) => i.email === p.email && i.status === 'ready');
                  const stale = (p.days_since_invite ?? 0) > 30;
                  return (
                    <li key={p.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`pending-row-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">
                          {p.name || p.email.split('@')[0]}
                        </p>
                        <p className="text-xs text-stone-500 truncate">{p.email}</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px] py-0 h-5">
                            invited {p.days_since_invite ?? '?'}d ago
                          </Badge>
                          {p.token_status === 'expired' && (
                            <Badge variant="outline" className="text-[10px] py-0 h-5 border-rose-300 text-rose-700 bg-rose-50">
                              <AlertTriangle className="w-3 h-3 mr-1" /> link expired
                            </Badge>
                          )}
                          {p.token_status === 'valid' && (
                            <Badge variant="outline" className="text-[10px] py-0 h-5 border-emerald-300 text-emerald-700 bg-emerald-50">
                              link valid
                            </Badge>
                          )}
                          {stale && p.token_status !== 'valid' && (
                            <Badge variant="outline" className="text-[10px] py-0 h-5 border-amber-300 text-amber-700 bg-amber-50">
                              stale
                            </Badge>
                          )}
                        </div>
                      </div>
                      {inv && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full text-xs"
                          onClick={() => copyOne(inv.invite_url, p.id)}
                          data-testid={`copy-invite-${p.id}`}
                        >
                          {copiedId === p.id ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copied</>
                            : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy fresh link</>}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {invites.length > 0 && (
              <div className="mt-5 rounded-xl bg-stone-50 border border-stone-200 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-medium text-stone-900">
                    Fresh invite links · {invites.filter((i) => i.status === 'ready').length}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={copyAll} className="text-xs" data-testid="copy-all-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mb-3">
                  Each link is valid for 30 days from now. Paste them into email, WhatsApp or SMS to the matching parent — do not forward to anyone else.
                </p>
                <ul className="space-y-2">
                  {invites.filter((i) => i.status === 'ready').map((i) => (
                    <li key={i.email} className="text-xs break-all">
                      <span className="font-medium text-stone-900">{i.name || i.email}</span>{' '}
                      <span className="text-stone-500">({i.email})</span>
                      <div className="mt-1 font-mono text-[11px] bg-white border border-stone-200 rounded px-2 py-1.5">
                        {i.invite_url}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
